/**
 * UI-driven bulk-patch flow — the default path on the page.
 *
 * Step machine:
 *   1. **edit**    — pick an org, edit rows inline (compact table)
 *   2. **review**  — dedicated review screen grouped by deal, showing
 *                    semver bumps, per-product diffs, and what Heartbeat
 *                    will receive on apply
 *   3. **applied** — confirmation screen with per-deal results +
 *                    Heartbeat push outcomes
 *
 * The transitions are explicit (no auto-advance): the user clicks
 * "Review changes" to leave edit, "Apply" to commit, or "Back to edit"
 * to return. Preview/apply results are kept in component state so the
 * user can step back without losing work.
 *
 * Locked candidate rows (terminal deals, finalized contracts, no
 * active deal, no products) are still surfaced in the edit table as
 * read-only context so users can see WHY a row isn't editable.
 */

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import SendIcon from '@mui/icons-material/Send';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  BulkPatchInputRow,
  BulkPatchResult,
  BulkPatchRowResult,
  ProductDiffEntry,
  useBulkPatch,
} from 'api/aws/deals/useBulkPatch';
import {
  CandidateRow,
  OrgRelationship,
  useBulkPatchCandidates,
} from 'api/aws/deals/useBulkPatchCandidates';
import { useEntitiesFromSearch } from 'api/search';
import React, {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { computeActiveOrgOptions, OrgOption } from './computeOrgOptions';

// ── Edit overlay state ──────────────────────────────────────────────────

interface RowEdit {
  price?: number | null;
  margin?: number | null;
  start_date?: string | null;
  end_date?: string | null;
}

type EditMap = Record<string, RowEdit>;

type EditAction =
  | {
      type: 'SET_FIELD';
      rowKey: string;
      field: keyof RowEdit;
      value: number | string | null;
    }
  | { type: 'RESET_ROW'; rowKey: string }
  | { type: 'RESET_ALL' };

function editReducer(state: EditMap, action: EditAction): EditMap {
  switch (action.type) {
    case 'SET_FIELD': {
      const existing = state[action.rowKey] ?? {};
      const next = { ...existing, [action.field]: action.value };
      const hasAny = Object.values(next).some((v) => v != null && v !== '');
      if (!hasAny) {
        const { [action.rowKey]: _drop, ...rest } = state;
        return rest;
      }
      return { ...state, [action.rowKey]: next };
    }
    case 'RESET_ROW': {
      const { [action.rowKey]: _drop, ...rest } = state;
      return rest;
    }
    case 'RESET_ALL':
      return {};
  }
}

// ── Step machine ───────────────────────────────────────────────────────

type Step = 'edit' | 'review' | 'applied';

/** Collapsed accordion-summary height — used to stack the step-2 and step-3
 *  sticky headers so both stay reachable at the top while scrolling. */
const STICKY_SUMMARY_H = 56;

// ── Org autocomplete row ────────────────────────────────────────────────

// ── Deal summary (derived from candidate rows) ─────────────────────────

interface DealSummary {
  deal_uuid: string;
  deal_pk?: string;
  deal_name: string;
  org_pk: string;
  org_name: string;
  org_relationship: OrgRelationship;
  stage?: string;
  status?: string;
  dealStatus?: string;
  semantic_version?: string;
  has_predecessor: boolean;
  deal_in_heartbeat: boolean;
  product_count: number;
  editable: boolean;
  block_reason?: string;
  /** All candidate rows under this deal (one per product). */
  rows: CandidateRow[];
}

/**
 * Collapse the flat candidate-row list into a per-deal summary list.
 * Skips orgs with no_active_deal block (no deal_uuid → nothing to select).
 */
function summarizeDealsFromCandidates(
  candidates: CandidateRow[]
): DealSummary[] {
  const map = new Map<string, DealSummary>();
  for (const c of candidates) {
    if (!c.deal_uuid) continue;
    const key = c.deal_uuid;
    const existing = map.get(key);
    if (existing) {
      existing.rows.push(c);
      if (c.product_pk) existing.product_count++;
      continue;
    }
    map.set(key, {
      deal_uuid: c.deal_uuid,
      deal_pk: c.deal_pk,
      deal_name: c.deal_name ?? '(unnamed deal)',
      org_pk: c.org_pk,
      org_name: c.org_name,
      org_relationship: c.org_relationship,
      stage: c.deal_stage,
      status: c.deal_status,
      dealStatus: c.deal_dealStatus,
      semantic_version: c.semantic_version,
      has_predecessor: Boolean(c.has_predecessor),
      deal_in_heartbeat: Boolean(c.deal_in_heartbeat),
      product_count: c.product_pk ? 1 : 0,
      editable: c.editable,
      block_reason: c.block_reason,
      rows: [c],
    });
  }
  return Array.from(map.values()).sort((a, b) => {
    // Root org first, then children alphabetically. Within org, by deal name.
    if (a.org_relationship !== b.org_relationship) {
      return a.org_relationship === 'self' ? -1 : 1;
    }
    if (a.org_name !== b.org_name)
      return a.org_name.localeCompare(b.org_name, undefined, {
        sensitivity: 'base',
      });
    return a.deal_name.localeCompare(b.deal_name, undefined, {
      sensitivity: 'base',
    });
  });
}

// ── Virtualized list ────────────────────────────────────────────────────

/**
 * A windowed vertical list: only the visible items (plus a small overscan)
 * are mounted, so a 150+ item list stays responsive even on "select all".
 * Item heights are measured dynamically, so variable-height rows — e.g. an
 * expandable deal card — reflow correctly. State that must survive an item
 * scrolling out of view (and unmounting) has to live in the parent, not in
 * the rendered item.
 */
function VirtualList<T>({
  items,
  getKey,
  estimateSize,
  gap,
  maxHeight,
  renderItem,
}: {
  items: T[];
  getKey: (item: T) => string;
  estimateSize: number;
  gap: number;
  maxHeight: string | number;
  renderItem: (item: T) => React.ReactNode;
}): React.ReactElement {
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan: 6,
    gap,
  });

  return (
    <Box ref={parentRef} sx={{ maxHeight, overflowY: 'auto', pr: 0.5 }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: virtualizer.getTotalSize(),
        }}
      >
        {virtualizer.getVirtualItems().map((vi) => (
          <Box
            key={getKey(items[vi.index])}
            data-index={vi.index}
            ref={virtualizer.measureElement}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${vi.start}px)`,
            }}
          >
            {renderItem(items[vi.index])}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Main component ──────────────────────────────────────────────────────

interface Props {
  onApplied?: (result: BulkPatchResult) => void;
}

const UiDrivenBulkPatchFlow: React.FC<Props> = ({ onApplied }) => {
  const { organizations, deals, products } = useEntitiesFromSearch();
  const [selectedOrg, setSelectedOrg] = useState<OrgOption | null>(null);
  const [selectedDealUuids, setSelectedDealUuids] = useState<Set<string>>(
    new Set()
  );
  const [edits, dispatch] = useReducer(editReducer, {});
  const [step, setStep] = useState<Step>('edit');
  const [previewResult, setPreviewResult] = useState<BulkPatchResult | null>(
    null
  );
  const [applyResult, setApplyResult] = useState<BulkPatchResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'editable' | 'blocked'
  >('all');
  // Collapsible step-2 / step-3 accordions. Both start open; selecting deals
  // auto-collapses step 2 to hand the room to editing, but its sticky header
  // stays pinned so selections can be managed again at any time.
  const [dealsOpen, setDealsOpen] = useState(true);
  const [editOpen, setEditOpen] = useState(true);

  const candidatesQuery = useBulkPatchCandidates(selectedOrg?.pk ?? null);
  const { mutateAsync: runBulkPatch, isPending: isMutating } = useBulkPatch();

  const orgOptions = useMemo<OrgOption[]>(
    () =>
      computeActiveOrgOptions(
        organizations as Array<Record<string, unknown>>,
        deals as Array<Record<string, unknown>>,
        products as Array<Record<string, unknown>>
      ),
    [organizations, deals, products]
  );

  const candidates = candidatesQuery.data?.rows ?? [];
  const editableCount = candidatesQuery.data?.total_editable_rows ?? 0;
  const blockedCount = candidatesQuery.data?.total_blocked_rows ?? 0;
  const editedCount = Object.keys(edits).length;

  // Group candidates by deal — drives the multi-select picker AND the
  // per-deal edit tables below it.
  const dealSummaries = useMemo(
    () => summarizeDealsFromCandidates(candidates),
    [candidates]
  );

  // Auto-select the only editable deal when there's exactly one. With many
  // deals the user must explicitly pick — same idea as the gmail "1 result
  // auto-opens, many results require a click" pattern.
  useEffect(() => {
    if (!candidatesQuery.data) return;
    setSelectedDealUuids((prev) => {
      // Only auto-select on a fresh load (selection is empty).
      if (prev.size > 0) return prev;
      const editable = dealSummaries.filter((d) => d.editable);
      if (editable.length === 1) return new Set([editable[0].deal_uuid]);
      return prev;
    });
  }, [candidatesQuery.data, dealSummaries]);

  // On the first deal selection, collapse step 2 and open step 3 so editing
  // gets the room. Only fires on the 0 → first transition; the user can
  // re-open either section from its pinned header afterwards.
  const prevSelCount = React.useRef(0);
  useEffect(() => {
    const n = selectedDealUuids.size;
    if (prevSelCount.current === 0 && n > 0) {
      setDealsOpen(false);
      setEditOpen(true);
    }
    prevSelCount.current = n;
  }, [selectedDealUuids]);

  // The selected-deal list — what the edit table shows + what payloadRows draws from.
  const selectedDeals = useMemo(
    () => dealSummaries.filter((d) => selectedDealUuids.has(d.deal_uuid)),
    [dealSummaries, selectedDealUuids]
  );
  const selectedDealRows = useMemo<CandidateRow[]>(
    () => selectedDeals.flatMap((d) => d.rows),
    [selectedDeals]
  );

  const payloadRows = useMemo<BulkPatchInputRow[]>(() => {
    const result: BulkPatchInputRow[] = [];
    let rowIndex = 1;
    for (const candidate of selectedDealRows) {
      const edit = edits[candidate.key];
      if (!edit || !candidate.editable || !candidate.product_pk) continue;
      result.push({
        row_index: rowIndex++,
        grobotic_org_pk: candidate.org_pk,
        deal_uuid: candidate.deal_uuid,
        product_pk: candidate.product_pk,
        product_name: candidate.product_name,
        new_price: edit.price ?? null,
        new_margin: edit.margin ?? null,
        new_start_date: edit.start_date ?? null,
        new_end_date: edit.end_date ?? null,
      });
    }
    return result;
  }, [selectedDealRows, edits]);

  const filteredCandidates = useMemo<CandidateRow[]>(() => {
    if (statusFilter === 'editable')
      return selectedDealRows.filter((c) => c.editable);
    if (statusFilter === 'blocked')
      return selectedDealRows.filter((c) => !c.editable);
    return selectedDealRows;
  }, [selectedDealRows, statusFilter]);

  // ── Handlers ─────────────────────────────────────────────────────
  const handleSelectOrg = (org: OrgOption | null): void => {
    setSelectedOrg(org);
    setSelectedDealUuids(new Set());
    dispatch({ type: 'RESET_ALL' });
    setPreviewResult(null);
    setApplyResult(null);
    setErrorMsg(null);
    setStep('edit');
  };

  const handleToggleDeal = (dealUuid: string): void => {
    setSelectedDealUuids((prev) => {
      const next = new Set(prev);
      if (next.has(dealUuid)) next.delete(dealUuid);
      else next.add(dealUuid);
      return next;
    });
  };

  const handleSelectAllDeals = (): void => {
    setSelectedDealUuids(
      new Set(dealSummaries.filter((d) => d.editable).map((d) => d.deal_uuid))
    );
  };

  const handleClearDealSelection = (): void => {
    setSelectedDealUuids(new Set());
    dispatch({ type: 'RESET_ALL' });
  };

  const handleResetAll = (): void => {
    dispatch({ type: 'RESET_ALL' });
    setPreviewResult(null);
    setApplyResult(null);
    setErrorMsg(null);
  };

  const handleReview = async (): Promise<void> => {
    setErrorMsg(null);
    try {
      const result = await runBulkPatch({ rows: payloadRows, dryRun: true });
      setPreviewResult(result);
      setStep('review');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  };

  const handleApply = async (): Promise<void> => {
    setErrorMsg(null);
    try {
      const result = await runBulkPatch({ rows: payloadRows, dryRun: false });
      setApplyResult(result);
      setStep('applied');
      onApplied?.(result);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  };

  const handleBackToEdit = (): void => {
    setStep('edit');
    setErrorMsg(null);
  };

  const handleStartOver = (): void => {
    dispatch({ type: 'RESET_ALL' });
    setPreviewResult(null);
    setApplyResult(null);
    setErrorMsg(null);
    setStep('edit');
  };

  // ── Render ───────────────────────────────────────────────────────
  return (
    <Stack spacing={2}>
      {step === 'edit' && (
        <>
          <OrgPickerSection
            orgOptions={orgOptions}
            selectedOrg={selectedOrg}
            onSelect={handleSelectOrg}
            candidatesQuery={candidatesQuery}
            editableCount={editableCount}
            blockedCount={blockedCount}
            disabled={isMutating}
          />
          {selectedOrg && dealSummaries.length > 0 && (
            <DealSelectorSection
              deals={dealSummaries}
              selected={selectedDealUuids}
              onToggle={handleToggleDeal}
              onSelectAll={handleSelectAllDeals}
              onClear={handleClearDealSelection}
              disabled={isMutating}
              expanded={dealsOpen}
              onExpandedChange={setDealsOpen}
            />
          )}
          {selectedOrg && selectedDeals.length > 0 && (
            <EditTableSection
              candidates={filteredCandidates}
              totalCandidates={selectedDealRows.length}
              editableCount={selectedDealRows.filter((c) => c.editable).length}
              blockedCount={selectedDealRows.filter((c) => !c.editable).length}
              edits={edits}
              dispatch={dispatch}
              editedCount={editedCount}
              payloadRowCount={payloadRows.length}
              onResetAll={handleResetAll}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onReview={handleReview}
              isMutating={isMutating}
              expanded={editOpen}
              onExpandedChange={setEditOpen}
            />
          )}
          {selectedOrg &&
            dealSummaries.length > 0 &&
            selectedDeals.length === 0 && (
              <Alert severity="info">
                Pick one or more deals above to start editing. Use{' '}
                <strong>Select all editable</strong> to quickly include every
                editable deal under this org.
              </Alert>
            )}
          {errorMsg && (
            <Alert severity="error" onClose={() => setErrorMsg(null)}>
              {errorMsg}
            </Alert>
          )}
        </>
      )}

      {step === 'review' && previewResult && (
        <ReviewSection
          previewResult={previewResult}
          onBack={handleBackToEdit}
          onApply={handleApply}
          isApplying={isMutating}
          errorMsg={errorMsg}
          onClearError={() => setErrorMsg(null)}
        />
      )}

      {step === 'applied' && applyResult && (
        <AppliedSummary result={applyResult} onStartOver={handleStartOver} />
      )}
    </Stack>
  );
};

// ════════════════════════════════════════════════════════════════════════
// Section: Org picker
// ════════════════════════════════════════════════════════════════════════

const OrgPickerSection: React.FC<{
  orgOptions: OrgOption[];
  selectedOrg: OrgOption | null;
  onSelect: (org: OrgOption | null) => void;
  candidatesQuery: ReturnType<typeof useBulkPatchCandidates>;
  editableCount: number;
  blockedCount: number;
  disabled: boolean;
}> = ({
  orgOptions,
  selectedOrg,
  onSelect,
  candidatesQuery,
  editableCount,
  blockedCount,
  disabled,
}) => {
  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 2, borderColor: 'divider', overflow: 'hidden' }}
    >
      {/* ── Header strip (matches the deal-card convention) ─────── */}
      <Box
        sx={{
          px: { xs: 2.5, md: 3 },
          py: 1.5,
          bgcolor: 'background.section',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: '0.08em',
          }}
        >
          Step 1
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          Pick an organization
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Autocomplete
            options={orgOptions}
            getOptionLabel={(o) => o.name}
            value={selectedOrg}
            onChange={(_, v) => onSelect(v)}
            sx={{ flex: 1, minWidth: 300 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search organizations"
                placeholder="Type to filter by name…"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.pk}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">
                    {option.name}
                    {option.isParent && (
                      <Chip
                        label="parent"
                        size="small"
                        sx={{ ml: 1, height: 18, fontSize: 10 }}
                      />
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {option.pk} · {option.type ?? 'no type'}
                  </Typography>
                </Box>
              </li>
            )}
            isOptionEqualToValue={(o, v) => o.pk === v.pk}
            disabled={disabled}
          />
          {selectedOrg && candidatesQuery.data && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                <strong>{candidatesQuery.data.root_org.name}</strong>
                {selectedOrg.isParent && ' (parent)'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {candidatesQuery.data.total_orgs} org(s) · {editableCount}{' '}
                editable
                {blockedCount > 0 && ` · ${blockedCount} blocked`}
              </Typography>
            </Box>
          )}
        </Stack>

        {candidatesQuery.isLoading && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 2 }}
          >
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading deals + products for this org…
            </Typography>
          </Stack>
        )}
        {candidatesQuery.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {candidatesQuery.error.message}
          </Alert>
        )}

        {/* Empty state — fills the space before an org is chosen. */}
        {!selectedOrg &&
          !candidatesQuery.isLoading &&
          !candidatesQuery.error && (
            <Box
              sx={{
                mt: 2.5,
                py: { xs: 4, md: 5 },
                px: 3,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'background.input',
                textAlign: 'center',
              }}
            >
              <CorporateFareIcon
                sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.secondary' }}
              >
                Search for an organization to begin
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.75, maxWidth: 520, mx: 'auto' }}
              >
                Pick a parent to load every child org&apos;s deals and products
                in one pass, or a single org to patch just its own deals.
                Terminal deals and finalized contracts are shown but locked.
              </Typography>
            </Box>
          )}
      </Box>
    </Paper>
  );
};

// ════════════════════════════════════════════════════════════════════════
// Section: Deal selector (multi-select)
// ════════════════════════════════════════════════════════════════════════

const DealSelectorSection: React.FC<{
  deals: DealSummary[];
  selected: Set<string>;
  onToggle: (dealUuid: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
  disabled: boolean;
  expanded: boolean;
  onExpandedChange: (v: boolean) => void;
}> = ({
  deals,
  selected,
  onToggle,
  onSelectAll,
  onClear,
  disabled,
  expanded,
  onExpandedChange,
}) => {
  const editableDeals = deals.filter((d) => d.editable);
  const lockedDeals = deals.filter((d) => !d.editable);
  const allEditableSelected =
    editableDeals.length > 0 &&
    editableDeals.every((d) => selected.has(d.deal_uuid));

  return (
    <Accordion
      disableGutters
      square
      expanded={expanded}
      onChange={(_, v) => onExpandedChange(v)}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        '&:before': { display: 'none' },
        bgcolor: 'background.paper',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          // Pin the header to the top of the scroll area so it stays reachable.
          position: 'sticky',
          top: 0,
          zIndex: 4,
          bgcolor: 'background.paper',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            pr: 1,
          },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            2. Select deals to edit
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {editableDeals.length} editable
            {lockedDeals.length > 0 && ` · ${lockedDeals.length} locked`}
            {' · '}
            <strong>{selected.size}</strong> selected
          </Typography>
        </Box>
        {/* Actions live in the header so selections can be managed even when
            the list is collapsed. stopPropagation keeps clicks off the toggle. */}
        <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
          {selected.size > 0 && (
            <Button size="small" onClick={onClear} disabled={disabled}>
              Clear
            </Button>
          )}
          {editableDeals.length > 1 && (
            <Button
              size="small"
              onClick={onSelectAll}
              disabled={disabled || allEditableSelected}
            >
              Select all editable
            </Button>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0.5 }}>
        {/* Virtualized — a parent org can carry 150+ deals; only the
            visible cards mount so "select all" stays responsive. */}
        <VirtualList
          items={deals}
          getKey={(deal) => deal.deal_uuid}
          estimateSize={66}
          gap={6}
          maxHeight="38vh"
          renderItem={(deal) => (
            <DealSelectorCard
              deal={deal}
              checked={selected.has(deal.deal_uuid)}
              onToggle={() => onToggle(deal.deal_uuid)}
              disabled={disabled}
            />
          )}
        />
      </AccordionDetails>
    </Accordion>
  );
};

const DealSelectorCard: React.FC<{
  deal: DealSummary;
  checked: boolean;
  onToggle: () => void;
  disabled: boolean;
}> = ({ deal, checked, onToggle, disabled }) => {
  const isLocked = !deal.editable;
  return (
    <Paper
      variant="outlined"
      onClick={isLocked || disabled ? undefined : onToggle}
      sx={{
        // Single flex row: checkbox + text block, vertically centered.
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        gap: 1.25,
        py: 0.75,
        px: 1.25,
        borderRadius: 1.5,
        borderColor: checked ? 'primary.main' : 'divider',
        bgcolor: isLocked
          ? 'action.hover'
          : checked
            ? 'action.selected'
            : 'background.paper',
        opacity: isLocked ? 0.7 : 1,
        cursor: isLocked || disabled ? 'default' : 'pointer',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover':
          isLocked || disabled ? undefined : { borderColor: 'primary.light' },
      }}
    >
      <Checkbox
        checked={checked}
        onChange={onToggle}
        disabled={isLocked || disabled}
        size="small"
        onClick={(e) => e.stopPropagation()}
        sx={{ p: 0.5, flexShrink: 0 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 600, lineHeight: 1.35 }}
        >
          {deal.org_relationship === 'child' && '↳ '}
          {deal.org_name}
          <Box
            component="span"
            sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.75 }}
          >
            · {deal.deal_name}
          </Box>
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.75,
            mt: 0.25,
          }}
        >
          {deal.semantic_version && (
            <Chip
              label={`v${deal.semantic_version}`}
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                fontFamily: 'monospace',
                bgcolor: 'action.hover',
              }}
            />
          )}
          {deal.stage && deal.status && (
            <Typography variant="caption" color="text.secondary">
              {deal.stage} · {deal.status}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            · {deal.product_count} product
            {deal.product_count === 1 ? '' : 's'}
          </Typography>
          {!deal.deal_in_heartbeat && (
            <Chip
              label="no contract"
              size="small"
              variant="outlined"
              color="warning"
              sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }}
            />
          )}
          {deal.deal_in_heartbeat && (
            <Chip
              label="in Heartbeat"
              size="small"
              variant="outlined"
              color="success"
              sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }}
            />
          )}
        </Box>
      </Box>

      {isLocked && deal.block_reason && (
        <Tooltip title={`Locked — ${deal.block_reason}`}>
          <Chip
            label="locked"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontFamily: 'monospace', flexShrink: 0 }}
          />
        </Tooltip>
      )}
    </Paper>
  );
};

// ════════════════════════════════════════════════════════════════════════
// Section: Edit table (compact rows)
// ════════════════════════════════════════════════════════════════════════

const EditTableSection: React.FC<{
  candidates: CandidateRow[];
  totalCandidates: number;
  editableCount: number;
  blockedCount: number;
  edits: EditMap;
  dispatch: React.Dispatch<EditAction>;
  editedCount: number;
  payloadRowCount: number;
  onResetAll: () => void;
  statusFilter: 'all' | 'editable' | 'blocked';
  onStatusFilterChange: (v: 'all' | 'editable' | 'blocked') => void;
  onReview: () => void;
  isMutating: boolean;
  expanded: boolean;
  onExpandedChange: (v: boolean) => void;
}> = ({
  candidates,
  totalCandidates,
  editableCount,
  blockedCount,
  edits,
  dispatch,
  editedCount,
  payloadRowCount,
  onResetAll,
  statusFilter,
  onStatusFilterChange,
  onReview,
  isMutating,
  expanded,
  onExpandedChange,
}) => {
  // Re-group the filtered candidate list back into per-deal buckets so we
  // can render one mini-table per deal (cleaner scan-by-deal than one
  // monolithic table when many deals are selected).
  const groupedByDeal = useMemo(
    () => summarizeDealsFromCandidates(candidates),
    [candidates]
  );
  // With a large parent (100+ deals) selected, mounting every deal's input
  // grid at once will lock up / crash the tab. Start deals collapsed past a
  // small threshold; a card still auto-expands when it already has edits.
  const expandByDefault = groupedByDeal.length <= 8;
  // Expand/collapse lives here (not in the card) so it survives the card
  // unmounting as it scrolls out of the virtualized list. Absent = default.
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>(
    {}
  );

  return (
    <Accordion
      disableGutters
      square
      expanded={expanded}
      onChange={(_, v) => onExpandedChange(v)}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        '&:before': { display: 'none' },
        bgcolor: 'background.paper',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          // Sticks just below the step-2 header so both stay reachable while
          // scrolling — collapse/expand either section on demand.
          position: 'sticky',
          top: STICKY_SUMMARY_H,
          zIndex: 3,
          bgcolor: 'background.paper',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            pr: 1,
          },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            3. Edit rows
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <strong>{editedCount}</strong> row{editedCount === 1 ? '' : 's'}{' '}
            edited
            {editedCount > 0 &&
              ` · ${payloadRowCount} change request${payloadRowCount === 1 ? '' : 's'} ready`}
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          onClick={(e) => e.stopPropagation()}
        >
          {editedCount > 0 && (
            <Button size="small" onClick={onResetAll}>
              Reset all edits
            </Button>
          )}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={statusFilter}
            onChange={(_, v) => v && onStatusFilterChange(v)}
          >
            <ToggleButton value="all">All ({totalCandidates})</ToggleButton>
            <ToggleButton value="editable">
              Editable ({editableCount})
            </ToggleButton>
            {blockedCount > 0 && (
              <ToggleButton value="blocked">
                Blocked ({blockedCount})
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0.5, pb: 2.5, px: { xs: 2, md: 3 } }}>
        <VirtualList
          items={groupedByDeal}
          getKey={(deal) => deal.deal_uuid}
          estimateSize={60}
          gap={16}
          maxHeight="52vh"
          renderItem={(deal) => {
            const defaultOpen =
              expandByDefault || deal.rows.some((r) => edits[r.key] != null);
            const open = openOverrides[deal.deal_uuid] ?? defaultOpen;
            return (
              <DealEditBlock
                deal={deal}
                edits={edits}
                dispatch={dispatch}
                isMutating={isMutating}
                open={open}
                onToggleOpen={() =>
                  setOpenOverrides((prev) => ({
                    ...prev,
                    [deal.deal_uuid]: !open,
                  }))
                }
              />
            );
          }}
        />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          sx={{ mt: 3, pt: 3, pb: 1, borderTop: 1, borderColor: 'divider' }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>{editedCount}</strong> row{editedCount === 1 ? '' : 's'}{' '}
            edited
            {editedCount > 0 &&
              ` · ${payloadRowCount} change request${payloadRowCount === 1 ? '' : 's'} ready`}
          </Typography>
          <Box sx={{ ml: { sm: 'auto' } }}>
            <Button
              variant="contained"
              onClick={onReview}
              disabled={editedCount === 0 || isMutating}
            >
              {isMutating ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Building review…
                </>
              ) : (
                'Review changes →'
              )}
            </Button>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

// ── Edit-step grid constants ──────────────────────────────────────────

/** Product name flexes; value columns are fixed so every input keeps
 *  identical geometry regardless of clean/edited state. */
const EDIT_GRID_COLUMNS = 'minmax(120px, 1fr) 116px 84px 104px 104px';
/** The app's established mass-update highlight — the same hex is hardcoded
 *  in every theme (obxops/dark/light), so it reads correctly in all of them. */
const EDIT_RAIL = 'rgba(232, 119, 34, 0.7)';
const EDIT_TINT = 'rgba(232, 119, 34, 0.12)';
const EDIT_ROW_BG = 'rgba(232, 119, 34, 0.06)';

interface FieldChange {
  label: string;
  oldValue: string;
  newValue: string;
  direction?: 'up' | 'down';
  delta?: string;
}

function formatMoney(value: number): string {
  return `$${value.toLocaleString()}`;
}

/**
 * Derive the before → after list for a row's pending edits. Price carries
 * an upsell/downsell direction when the deal has a baseline to compare
 * against (an explicit predecessor, or a deal already in Heartbeat).
 */
function computeRowChanges(
  candidate: CandidateRow,
  edit: RowEdit
): FieldChange[] {
  const changes: FieldChange[] = [];
  if (edit.price != null && edit.price !== candidate.current_price) {
    const hasBaseline =
      Boolean(candidate.has_predecessor || candidate.deal_in_heartbeat) &&
      candidate.current_price != null;
    let direction: 'up' | 'down' | undefined;
    let delta: string | undefined;
    if (hasBaseline && candidate.current_price != null) {
      direction = edit.price > candidate.current_price ? 'up' : 'down';
      const magnitude = Math.abs(edit.price - candidate.current_price);
      delta = `${direction === 'up' ? '+' : '−'}${formatMoney(magnitude)}`;
    }
    changes.push({
      label: 'Price',
      oldValue:
        candidate.current_price != null
          ? formatMoney(candidate.current_price)
          : '—',
      newValue: formatMoney(edit.price),
      direction,
      delta,
    });
  }
  if (edit.margin != null && edit.margin !== candidate.current_margin) {
    changes.push({
      label: 'Margin',
      oldValue:
        candidate.current_margin != null ? `${candidate.current_margin}%` : '—',
      newValue: `${edit.margin}%`,
    });
  }
  const currentStart = candidate.current_start_date?.slice(0, 10);
  if (edit.start_date != null && edit.start_date !== currentStart) {
    changes.push({
      label: 'Start',
      oldValue: currentStart ?? '—',
      newValue: edit.start_date,
    });
  }
  const currentEnd = candidate.current_end_date?.slice(0, 10);
  if (edit.end_date != null && edit.end_date !== currentEnd) {
    changes.push({
      label: 'End',
      oldValue: currentEnd ?? '—',
      newValue: edit.end_date,
    });
  }
  return changes;
}

/**
 * Fixed-geometry field styling. Height, width, and border are constant
 * between clean and edited states — an edit is signalled only by a
 * background tint, never by resizing or restyling the box.
 */
function fieldSx(isDirty: boolean): SxProps<Theme> {
  return {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: 34,
      fontSize: 13,
      bgcolor: isDirty ? EDIT_TINT : 'background.input',
    },
    '& .MuiOutlinedInput-input': { py: 0, px: 1.25, fontSize: 13 },
  };
}

/** A single value input (number or date) with constant geometry. */
const EditFieldCell: React.FC<{
  type: 'number' | 'date';
  currentValue?: number | string;
  editValue?: number | string | null;
  onChange: (value: number | string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}> = ({ type, currentValue, editValue, onChange, placeholder, disabled }) => {
  const isDirty = editValue != null;

  // Buffer the raw input text locally so partially-typed numeric values
  // ('.', '-', '1.', '1e') survive keystrokes. A `type="number"` value can't
  // represent those intermediate strings, so committing straight to the
  // parsed number and re-rendering from it wiped anything that didn't yet
  // parse to a finite float — e.g. a leading '.' was eaten before '.5'.
  const [rawText, setRawText] = useState<string>(
    editValue != null ? String(editValue) : ''
  );

  // Re-sync the buffer when the controlled value changes from the outside
  // (reset / programmatic edit). We overwrite only when the incoming value
  // disagrees with what the buffer already parses to, so an in-flight
  // keystroke that already produced this value is never clobbered.
  useEffect(() => {
    const bufferValue =
      rawText === ''
        ? null
        : type === 'number'
          ? Number.isFinite(parseFloat(rawText))
            ? parseFloat(rawText)
            : null
          : rawText;
    if (bufferValue !== (editValue ?? null)) {
      setRawText(editValue != null ? String(editValue) : '');
    }
    return;
    // rawText is intentionally omitted from the deps: this effect reacts to
    // external changes of editValue, not to our own in-flight keystrokes.
  }, [editValue, type]);

  return (
    <TextField
      size="small"
      type={type}
      value={rawText}
      placeholder={
        currentValue != null ? String(currentValue).slice(0, 10) : placeholder
      }
      onChange={(e) => {
        const raw = e.target.value;
        setRawText(raw);
        if (raw === '') {
          onChange(null);
          return;
        }
        if (type === 'number') {
          const parsed = parseFloat(raw);
          onChange(Number.isFinite(parsed) ? parsed : null);
          return;
        }
        onChange(raw);
      }}
      disabled={disabled}
      InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      sx={fieldSx(isDirty)}
    />
  );
};

/**
 * One product row: a fixed-column grid of inputs, plus a detail line that
 * appears below only when the row is edited. The orange rail + tint live
 * on the row wrapper, so the inputs themselves never reflow.
 */
const DealEditRow: React.FC<{
  candidate: CandidateRow;
  edit?: RowEdit;
  onSetField: (field: keyof RowEdit, value: number | string | null) => void;
  onReset: () => void;
  disabled: boolean;
}> = ({ candidate, edit, onSetField, onReset, disabled }) => {
  if (!candidate.editable) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.75,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <LockIcon
          fontSize="small"
          sx={{ color: 'warning.main', fontSize: 16, flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          noWrap
          sx={{ fontSize: 13 }}
          color="text.secondary"
        >
          {candidate.product_name ?? '(no product)'}
        </Typography>
        <Chip
          label={candidate.block_reason ?? 'locked'}
          size="small"
          color="warning"
          variant="outlined"
          sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }}
        />
        {candidate.block_message && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {candidate.block_message}
          </Typography>
        )}
      </Box>
    );
  }

  const isDirty = edit != null;
  const changes = edit != null ? computeRowChanges(candidate, edit) : [];
  const productTooltip = `${candidate.product_name}${candidate.service_line ? ` · ${candidate.service_line}` : ''}`;

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        borderLeft: '2px solid',
        borderLeftColor: isDirty ? EDIT_RAIL : 'transparent',
        bgcolor: isDirty ? EDIT_ROW_BG : 'transparent',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: EDIT_GRID_COLUMNS,
          columnGap: 1.25,
          alignItems: 'center',
          px: 1.75,
          py: 1,
        }}
      >
        <Tooltip title={productTooltip} placement="top-start">
          <Typography
            variant="body2"
            noWrap
            sx={{ fontSize: 13, fontWeight: 500, minWidth: 0 }}
          >
            {candidate.product_name}
          </Typography>
        </Tooltip>
        <EditFieldCell
          type="number"
          currentValue={candidate.current_price}
          editValue={edit?.price}
          onChange={(v) => onSetField('price', v)}
          placeholder="$"
          disabled={disabled}
        />
        <EditFieldCell
          type="number"
          currentValue={candidate.current_margin}
          editValue={edit?.margin}
          onChange={(v) => onSetField('margin', v)}
          placeholder="%"
          disabled={disabled}
        />
        <EditFieldCell
          type="date"
          currentValue={candidate.current_start_date}
          editValue={edit?.start_date}
          onChange={(v) => onSetField('start_date', v)}
          disabled={disabled}
        />
        <EditFieldCell
          type="date"
          currentValue={candidate.current_end_date}
          editValue={edit?.end_date}
          onChange={(v) => onSetField('end_date', v)}
          disabled={disabled}
        />
      </Box>
      {isDirty && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            px: 2,
            pb: 1,
          }}
        >
          {changes.map((change) => (
            <Stack
              key={change.label}
              direction="row"
              spacing={0.75}
              alignItems="baseline"
            >
              <Typography variant="caption" color="text.secondary">
                {change.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'line-through',
                }}
              >
                {change.oldValue}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                →
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color:
                    change.direction === 'down'
                      ? 'text.revise'
                      : change.direction === 'up'
                        ? 'text.success'
                        : 'text.primary',
                }}
              >
                {change.newValue}
                {change.direction &&
                  ` · ${change.direction === 'up' ? '↑ Upsell' : '↓ Downsell'}${change.delta ? ` ${change.delta}` : ''}`}
              </Typography>
            </Stack>
          ))}
          <Button
            size="small"
            onClick={onReset}
            disabled={disabled}
            sx={{ ml: 'auto', minWidth: 0, fontSize: 11 }}
          >
            Reset row
          </Button>
        </Box>
      )}
    </Box>
  );
};

/**
 * One deal, rendered as a self-contained card: a clickable header strip
 * (org, deal, version, contract state) over a fixed-column product grid.
 *
 * The body (column header + input rows) is only mounted while the card is
 * expanded. This is what keeps a 150-deal parent usable — collapsed cards
 * carry no inputs, so "select all" mounts a few hundred light headers
 * instead of thousands of live TextFields. Edits live in the reducer and
 * survive collapse/expand; a collapsed card shows how many rows it holds
 * and how many are already edited.
 */
const DealEditBlock: React.FC<{
  deal: DealSummary;
  edits: EditMap;
  dispatch: React.Dispatch<EditAction>;
  isMutating: boolean;
  open: boolean;
  onToggleOpen: () => void;
}> = ({ deal, edits, dispatch, isMutating, open, onToggleOpen }) => {
  const editableCount = deal.rows.filter((r) => r.editable).length;
  const editedCount = deal.rows.filter((r) => edits[r.key] != null).length;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'transparent',
        // Keep full height inside the max-height scroll area — without this,
        // the flex column compresses each card and clips its product rows.
        flexShrink: 0,
      }}
    >
      {/* ── Deal header (click to expand/collapse) ──────────────── */}
      <Box
        onClick={onToggleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          px: 1.5,
          py: 1.25,
          cursor: 'pointer',
          userSelect: 'none',
          bgcolor: 'background.section',
          borderBottom: open ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 20,
            flexShrink: 0,
            color: 'text.secondary',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.15s',
          }}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {deal.org_relationship === 'child' && '↳ '}
          {deal.org_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          · {deal.deal_name}
        </Typography>
        {deal.semantic_version && (
          <Chip
            label={`v${deal.semantic_version}`}
            size="small"
            sx={{
              height: 18,
              fontSize: 10,
              fontFamily: 'monospace',
              bgcolor: 'background.input',
            }}
          />
        )}
        {deal.deal_in_heartbeat ? (
          <Chip
            label="in Heartbeat"
            size="small"
            variant="outlined"
            color="success"
            sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }}
          />
        ) : (
          <Chip
            label="no contract"
            size="small"
            variant="outlined"
            color="warning"
            sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }}
          />
        )}
        {editedCount > 0 && (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: 11,
              fontWeight: 700,
              color: EDIT_RAIL,
            }}
          >
            <Box
              sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: EDIT_RAIL }}
            />
            {editedCount} edited
          </Box>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 'auto', pl: 1 }}
        >
          {editableCount} product{editableCount === 1 ? '' : 's'}
        </Typography>
      </Box>

      {open && (
        <>
          {/* ── Column header ───────────────────────────────────── */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: EDIT_GRID_COLUMNS,
              columnGap: 1.25,
              px: 1.75,
              py: 0.75,
              borderBottom: 1,
              borderColor: 'divider',
              '& > span': {
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              },
            }}
          >
            <span>Product</span>
            <span>Price</span>
            <span>Margin</span>
            <span>Start</span>
            <span>End</span>
          </Box>

          {/* ── Product rows ──────────────────────────────────────── */}
          {deal.rows.map((candidate) => (
            <DealEditRow
              key={candidate.key}
              candidate={candidate}
              edit={edits[candidate.key]}
              onSetField={(field, value) =>
                dispatch({
                  type: 'SET_FIELD',
                  rowKey: candidate.key,
                  field,
                  value,
                })
              }
              onReset={() =>
                dispatch({ type: 'RESET_ROW', rowKey: candidate.key })
              }
              disabled={isMutating}
            />
          ))}
        </>
      )}
    </Paper>
  );
};

// ════════════════════════════════════════════════════════════════════════
// Section: Review screen
// ════════════════════════════════════════════════════════════════════════

interface DealGroup {
  deal_uuid: string;
  deal_name: string;
  org_name: string;
  org_pk: string;
  new_semantic_version?: string;
  diffs: ProductDiffEntry[];
  status: 'would_apply' | 'blocked' | 'error' | 'applied';
  reason?: string;
  message?: string;
  /** True iff the BE predicts a Heartbeat push for this deal (no skip_reason on heartbeat_push). */
  willPushHeartbeat: boolean;
  heartbeatSkipReason?: string;
  /** Change Order (in-place semver bump) or New Contract (successor deal). */
  classification?: 'change_order' | 'new_contract';
}

function groupResultsByDeal(results: BulkPatchRowResult[]): DealGroup[] {
  const map = new Map<string, DealGroup>();
  results.forEach((r, i) => {
    // Resolved rows for the same deal merge (multiple product rows collapse
    // into one deal card). Unresolved rows — blocked/errored before a deal
    // could be resolved — must NOT merge: each is a distinct failure with its
    // own reason/message. Keying them by org alone dropped every reason after
    // the first and undercounted the blocked/errored totals, so key each
    // unresolved row uniquely by its position instead.
    const key = r.resolved_deal_uuid ?? `_unresolved_${r.grobotic_org_pk}_${i}`;
    const existing = map.get(key);
    if (existing) {
      existing.diffs.push(...r.diff);
      return;
    }
    const heartbeatSkipReason = r.heartbeat_push?.skip_reason;
    map.set(key, {
      deal_uuid: r.resolved_deal_uuid ?? '',
      deal_name: r.resolved_deal_name ?? '(no deal)',
      org_name: r.org_name ?? '(unknown org)',
      org_pk: r.grobotic_org_pk,
      new_semantic_version: r.new_semantic_version,
      diffs: [...r.diff],
      status: r.status,
      reason: r.reason,
      message: r.message,
      willPushHeartbeat: !heartbeatSkipReason,
      heartbeatSkipReason,
      classification: r.classification,
    });
  });
  return Array.from(map.values());
}

/**
 * A section container matching the deal-card convention: a
 * background.section header strip (optional eyebrow + title +
 * right-aligned action) over a padded body.
 */
const SectionCard: React.FC<{
  eyebrow?: string;
  title: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ eyebrow, title, action, children }) => {
  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 2, borderColor: 'divider', overflow: 'hidden' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          px: { xs: 2.5, md: 3 },
          py: 1.5,
          bgcolor: 'background.section',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ minWidth: 0, mr: 'auto' }}>
          {eyebrow && (
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: '0.08em',
                lineHeight: 1.4,
              }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {title}
          </Typography>
        </Box>
        {action}
      </Box>
      <Box sx={{ p: { xs: 2.5, md: 3 } }}>{children}</Box>
    </Paper>
  );
};

/** Direction of a numeric diff, for coloring the new value on review. */
function diffDirection(d: ProductDiffEntry): 'up' | 'down' | undefined {
  if (
    typeof d.old_value === 'number' &&
    typeof d.new_value === 'number' &&
    d.new_value !== d.old_value
  ) {
    return d.new_value > d.old_value ? 'up' : 'down';
  }
  return undefined;
}

const ReviewSection: React.FC<{
  previewResult: BulkPatchResult;
  onBack: () => void;
  onApply: () => void;
  isApplying: boolean;
  errorMsg: string | null;
  onClearError: () => void;
}> = ({
  previewResult,
  onBack,
  onApply,
  isApplying,
  errorMsg,
  onClearError,
}) => {
  const groups = useMemo(
    () => groupResultsByDeal(previewResult.results),
    [previewResult]
  );
  const willApply = groups.filter((g) => g.status === 'would_apply');
  const blocked = groups.filter(
    (g) => g.status === 'blocked' || g.status === 'error'
  );
  const totalProductChanges = willApply.reduce((s, g) => s + g.diffs.length, 0);
  const willPush = willApply.filter((g) => g.willPushHeartbeat);
  const willSkipPush = willApply.filter((g) => !g.willPushHeartbeat);

  return (
    <Stack spacing={4}>
      {/* ── Summary ─────────────────────────────────────────── */}
      <SectionCard
        eyebrow="Preview · nothing written yet"
        title="Review changes before applying"
        action={
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={onBack}
          >
            Back to edit
          </Button>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Review each deal below, then confirm to commit.
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <CountCard
            label="Deals to update"
            value={willApply.length}
            color="success"
          />
          <CountCard
            label="Product changes"
            value={totalProductChanges}
            color="success"
          />
          <CountCard
            label="Heartbeat pushes"
            value={willPush.length}
            color="default"
          />
          {willSkipPush.length > 0 && (
            <CountCard
              label="Heartbeat skipped"
              value={willSkipPush.length}
              color="warning"
            />
          )}
          {blocked.length > 0 && (
            <CountCard
              label="Blocked / errored"
              value={blocked.length}
              color="warning"
            />
          )}
        </Stack>
      </SectionCard>

      {/* ── Per-deal breakdown ─────────────────────────────── */}
      <SectionCard
        title={`What will change (${willApply.length} deal${willApply.length === 1 ? '' : 's'})`}
      >
        <Stack spacing={2}>
          {willApply.length === 0 && (
            <Alert severity="info">
              No deals would be updated with the current edits.
            </Alert>
          )}
          {willApply.map((g) => (
            <DealReviewCard key={g.deal_uuid} group={g} />
          ))}
        </Stack>

        {blocked.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: 'warning.dark', mb: 1.5 }}
            >
              Blocked or errored ({blocked.length})
            </Typography>
            <Stack spacing={1}>
              {blocked.map((g) => (
                <Alert
                  key={`${g.deal_uuid}-${g.org_pk}`}
                  severity={g.status === 'error' ? 'error' : 'warning'}
                  icon={g.status === 'error' ? <ErrorIcon /> : <WarningIcon />}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {g.org_name} — {g.deal_name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {g.reason}
                  </Typography>
                  {g.message && (
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      {g.message}
                    </Typography>
                  )}
                </Alert>
              ))}
            </Stack>
          </>
        )}
      </SectionCard>

      {/* ── Heartbeat impact ───────────────────────────────── */}
      <SectionCard title="What gets pushed to Heartbeat">
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            On apply, each updated deal that{' '}
            <strong>already exists in Heartbeat</strong> (has a contract
            attached) triggers one Heartbeat push (typically{' '}
            <strong>Flow B</strong> — service-change submission to the existing
            Heartbeat account). Pushes fire sequentially, one deal at a time.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Deals without a contract yet (no PandaDoc document and no
            no-signature contract) are Grobotic-internal only — bulk-patch{' '}
            <strong>does not</strong> create a fresh Heartbeat contract for
            them. Those updates are skipped on the Heartbeat side and the deal
            stays in sync inside Grobotic.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A failed push <strong>does not</strong> roll back the MOTHERSHIP
            write — the patch is committed and the failure is logged so it can
            be re-fired from the deal page.
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{ pt: 0.5 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              label={`${willPush.length} push${willPush.length === 1 ? '' : 'es'}`}
              size="small"
              color={willPush.length > 0 ? 'success' : 'default'}
              sx={{ fontFamily: 'monospace' }}
            />
            {willSkipPush.length > 0 && (
              <Chip
                label={`${willSkipPush.length} skipped (no contract)`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontFamily: 'monospace' }}
              />
            )}
          </Stack>
        </Stack>
      </SectionCard>

      {/* ── Apply bar ───────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 2,
          borderColor: 'divider',
          position: 'sticky',
          bottom: 16,
          bgcolor: 'background.paper',
          boxShadow: 4,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Ready to apply <strong>{willApply.length}</strong> change(s)?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              MOTHERSHIP writes + {willPush.length} Heartbeat push
              {willPush.length === 1 ? '' : 'es'}
              {willSkipPush.length > 0 &&
                ` (${willSkipPush.length} skipped — no contract yet)`}{' '}
              will fire on confirm.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={onBack}
              disabled={isApplying}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={
                isApplying ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              onClick={onApply}
              disabled={isApplying || willApply.length === 0}
              size="large"
            >
              {isApplying ? 'Applying…' : `Apply ${willApply.length} change(s)`}
            </Button>
          </Stack>
        </Stack>
        {errorMsg && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={onClearError}>
            {errorMsg}
          </Alert>
        )}
      </Paper>
    </Stack>
  );
};

const DealReviewCard: React.FC<{ group: DealGroup }> = ({ group }) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'transparent',
        transition: 'border-color 0.15s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      {/* ── Deal header strip ───────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          px: 1.75,
          py: 1.25,
          bgcolor: 'background.section',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ minWidth: 0, mr: 'auto' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
            {group.org_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {group.deal_name}
          </Typography>
        </Box>
        <ClassificationChip classification={group.classification} />
        {group.willPushHeartbeat ? (
          <Tooltip title="Heartbeat will receive a service-change push on apply">
            <Chip
              label="Heartbeat push"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontFamily: 'monospace' }}
            />
          </Tooltip>
        ) : (
          <Tooltip
            title={`Heartbeat skipped — ${group.heartbeatSkipReason ?? 'no contract attached yet'}`}
          >
            <Chip
              label="No Heartbeat push"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontFamily: 'monospace' }}
            />
          </Tooltip>
        )}
        {group.new_semantic_version && (
          <Chip
            label={`→ v${group.new_semantic_version}`}
            size="small"
            color="success"
            sx={{ fontFamily: 'monospace' }}
          />
        )}
      </Box>

      {/* ── Product diffs ───────────────────────────────────────── */}
      <Stack spacing={0.75} sx={{ px: 2, py: 1.5 }}>
        {group.diffs.map((d, i) => {
          const direction = diffDirection(d);
          return (
            <Stack
              key={i}
              direction="row"
              spacing={1}
              alignItems="baseline"
              flexWrap="wrap"
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, minWidth: 140 }}
                noWrap
              >
                {d.product_name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 56 }}
              >
                {d.field}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  color: 'text.secondary',
                  textDecoration: 'line-through',
                }}
              >
                {formatValue(d.old_value)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                →
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  color:
                    direction === 'down'
                      ? 'text.revise'
                      : direction === 'up'
                        ? 'text.success'
                        : 'text.primary',
                }}
              >
                {formatValue(d.new_value)}
                {direction && ` ${direction === 'up' ? '↑' : '↓'}`}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
};

// ════════════════════════════════════════════════════════════════════════
// Section: Applied summary
// ════════════════════════════════════════════════════════════════════════

const AppliedSummary: React.FC<{
  result: BulkPatchResult;
  onStartOver: () => void;
}> = ({ result, onStartOver }) => {
  const appliedRows = result.results.filter((r) => r.status === 'applied');
  const failedRows = result.results.filter(
    (r) => r.status === 'blocked' || r.status === 'error'
  );
  const heartbeatOk = appliedRows.filter(
    (r) => r.heartbeat_push?.success
  ).length;
  const heartbeatFailed = appliedRows.filter(
    (r) => r.heartbeat_push && !r.heartbeat_push.success
  ).length;

  return (
    <Stack spacing={4}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 2,
          borderColor: 'success.main',
          borderWidth: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 36 }} />
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}
            >
              Applied {result.applied} change{result.applied === 1 ? '' : 's'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              MOTHERSHIP committed. {heartbeatOk} Heartbeat push
              {heartbeatOk === 1 ? '' : 'es'} succeeded
              {heartbeatFailed > 0 && `, ${heartbeatFailed} failed`}.
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <CountCard label="Applied" value={result.applied} color="success" />
          <CountCard label="Heartbeat OK" value={heartbeatOk} color="success" />
          {heartbeatFailed > 0 && (
            <CountCard
              label="Heartbeat failed"
              value={heartbeatFailed}
              color="warning"
            />
          )}
          {failedRows.length > 0 && (
            <CountCard
              label="Blocked / errored"
              value={failedRows.length}
              color="error"
            />
          )}
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, borderColor: 'divider' }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Per-deal results
        </Typography>
        <Stack spacing={1.25}>
          {result.results.map((r, i) => (
            <AppliedRowCard key={i} row={r} />
          ))}
        </Stack>
      </Paper>

      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" onClick={onStartOver}>
          Start a new bulk patch
        </Button>
      </Stack>
    </Stack>
  );
};

const AppliedRowCard: React.FC<{ row: BulkPatchRowResult }> = ({ row }) => {
  const statusColor =
    row.status === 'applied'
      ? 'success.main'
      : row.status === 'blocked'
        ? 'warning.main'
        : 'error.main';

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderRadius: 1.5, borderColor: 'divider' }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: statusColor,
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.org_name ?? '(unknown org)'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.resolved_deal_name ?? '(no deal)'}
            {row.new_semantic_version && ` → v${row.new_semantic_version}`}
          </Typography>
        </Box>
        {row.heartbeat_push && (
          <Tooltip
            title={
              row.heartbeat_push.success
                ? `Heartbeat: flow=${row.heartbeat_push.flow ?? '?'} · ${row.heartbeat_push.emitted_submissions ?? 0} submission(s)`
                : `Heartbeat failed${row.heartbeat_push.skip_reason ? ` — ${row.heartbeat_push.skip_reason}` : ''}`
            }
          >
            <Chip
              size="small"
              label={`Heartbeat ${row.heartbeat_push.success ? '✓' : '⚠'}`}
              color={row.heartbeat_push.success ? 'success' : 'warning'}
              variant="outlined"
              sx={{ fontFamily: 'monospace' }}
            />
          </Tooltip>
        )}
        {(row.status === 'blocked' || row.status === 'error') && (
          <Tooltip title={row.message ?? ''}>
            <Chip
              size="small"
              label={row.reason ?? row.status}
              color={row.status === 'error' ? 'error' : 'warning'}
              sx={{ fontFamily: 'monospace' }}
            />
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
};

// ════════════════════════════════════════════════════════════════════════
// Shared bits
// ════════════════════════════════════════════════════════════════════════

const ClassificationChip: React.FC<{
  classification: 'change_order' | 'new_contract' | undefined;
}> = ({ classification }) => {
  if (!classification) return null;
  if (classification === 'new_contract') {
    return (
      <Tooltip title="Products were added or removed — creates a new successor deal with a new no-signature contract.">
        <Chip
          label="New Contract"
          size="small"
          color="primary"
          sx={{ fontFamily: 'monospace', fontWeight: 600 }}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Only product data changed (price, margin, dates) — in-place semver bump (Change Order).">
      <Chip
        label="Change Order"
        size="small"
        color="info"
        sx={{ fontFamily: 'monospace', fontWeight: 600 }}
      />
    </Tooltip>
  );
};

const CountCard: React.FC<{
  label: string;
  value: number;
  color: 'success' | 'warning' | 'error' | 'default';
}> = ({ label, value, color }) => {
  const palette: Record<typeof color, string> = {
    success: 'success.main',
    warning: 'warning.main',
    error: 'error.main',
    default: 'text.primary',
  };
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        minWidth: 116,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.input',
      }}
    >
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, lineHeight: 1.1, color: palette[color] }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
};

function formatValue(v: number | string | null): string {
  if (v == null || v === '') return '—';
  return String(v);
}

export default UiDrivenBulkPatchFlow;
